import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { adminApi, ApiError } from './api/client';
import { DraftEditor } from './components/DraftEditor';
import type { MapDraftShape } from './lib/map-draft';
import type {
  EditionDraftV1,
  StudioSection,
  ValidationIssue,
  ValidationResult,
} from './types';

const MapEditor = lazy(() =>
  import('./components/MapEditor').then((module) => ({ default: module.MapEditor })),
);
const ModerationStudio = lazy(() =>
  import('./components/ModerationStudio').then((module) => ({ default: module.ModerationStudio })),
);
const AuditLog = lazy(() =>
  import('./components/AuditLog').then((module) => ({ default: module.AuditLog })),
);

type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'conflict' | 'error';

function saveLabel(state: SaveState): string {
  switch (state) {
    case 'clean':
      return 'Entwurf geladen';
    case 'dirty':
      return 'Ungespeicherte Änderungen';
    case 'saving':
      return 'Speichert …';
    case 'saved':
      return 'Gespeichert';
    case 'conflict':
      return 'Versionskonflikt';
    case 'error':
      return 'Speichern fehlgeschlagen';
  }
}

interface NewEditionInput {
  title: string;
  type: 'summer' | 'winter';
  startsAt: string;
  endsAt: string;
  timezone: string;
}

function defaultEditionInput(): NewEditionInput {
  const nextYear = new Date().getFullYear() + 1;
  return {
    title: `LIR ${nextYear}`,
    type: 'summer' as const,
    startsAt: `${nextYear}-06-18T12:00`,
    endsAt: `${nextYear}-06-21T12:00`,
    timezone: 'Europe/Prague',
  };
}

function toIso(local: string): string {
  return new Date(local).toISOString();
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unbekannter Fehler';
}

function unlinkStageMapFeature(
  stage: EditionDraftV1['stages'][number],
): EditionDraftV1['stages'][number] {
  const next = { ...stage };
  delete next.mapFeatureId;
  return next;
}

export default function App() {
  const queryClient = useQueryClient();
  const [selectedEditionId, setSelectedEditionId] = useState<string>();
  const [section, setSection] = useState<StudioSection>('content');
  const [draft, setDraft] = useState<EditionDraftV1>();
  const [saveState, setSaveState] = useState<SaveState>('clean');
  const [validation, setValidation] = useState<ValidationResult>();
  const [focusFeatureId, setFocusFeatureId] = useState<string>();
  const [newEditionOpen, setNewEditionOpen] = useState(false);
  const [newEdition, setNewEdition] = useState(defaultEditionInput);
  const [creating, setCreating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState<string>();

  const draftRef = useRef<EditionDraftV1 | undefined>(undefined);
  const etagRef = useRef('"0"');
  const dirtyRef = useRef(false);
  const savePromiseRef = useRef<Promise<void> | null>(null);
  const selectedEditionRef = useRef<string | undefined>(undefined);

  const editions = useQuery({
    queryKey: ['admin', 'editions'],
    queryFn: adminApi.listEditions,
    retry: (count, error) => !(error instanceof ApiError && [401, 403].includes(error.status)) && count < 2,
  });

  useEffect(() => {
    if (selectedEditionId === undefined && editions.data?.[0] !== undefined) {
      setSelectedEditionId(editions.data[0].id);
    }
  }, [editions.data, selectedEditionId]);

  useEffect(() => {
    selectedEditionRef.current = selectedEditionId;
  }, [selectedEditionId]);

  const draftQuery = useQuery({
    queryKey: ['admin', 'edition-draft', selectedEditionId],
    queryFn: () => adminApi.getDraft(selectedEditionId!),
    enabled: selectedEditionId !== undefined,
  });

  const revisions = useQuery({
    queryKey: ['admin', 'edition-revisions', selectedEditionId],
    queryFn: () => adminApi.listRevisions(selectedEditionId!),
    enabled: selectedEditionId !== undefined,
  });

  useEffect(() => {
    if (draftQuery.data === undefined) return;
    setDraft(draftQuery.data.draft);
    draftRef.current = draftQuery.data.draft;
    etagRef.current = draftQuery.data.etag;
    dirtyRef.current = false;
    setValidation(undefined);
    setSaveState('clean');
  }, [draftQuery.data]);

  const changeDraft = (next: EditionDraftV1) => {
    setDraft(next);
    draftRef.current = next;
    dirtyRef.current = true;
    setSaveState('dirty');
    setValidation(undefined);
  };

  const acceptServerDraft = (next: { draft: EditionDraftV1; etag: string }) => {
    setDraft(next.draft);
    draftRef.current = next.draft;
    etagRef.current = next.etag;
    dirtyRef.current = false;
    setValidation(undefined);
    setSaveState('saved');
  };

  const changeMap = (map: MapDraftShape) => {
    const current = draftRef.current;
    if (current === undefined) return;
    const featureIds = new Set(map.features.features.map((feature) => feature.id));
    const stages = current.stages.map((stage) =>
      stage.mapFeatureId !== undefined && !featureIds.has(stage.mapFeatureId)
        ? unlinkStageMapFeature(stage)
        : stage,
    );
    changeDraft({ ...current, map, stages });
  };

  const linkMapStage = (
    map: MapDraftShape,
    featureId: string,
    stageId: string | undefined,
  ) => {
    const current = draftRef.current;
    if (current === undefined) return;
    const features = map.features.features.map((feature) => {
      if (feature.id === featureId || stageId === undefined || feature.properties.stageId !== stageId) {
        return feature;
      }
      const properties = { ...feature.properties };
      delete properties.stageId;
      return { ...feature, properties };
    });
    const stages = current.stages.map((stage) => {
      if (stage.id === stageId) return { ...stage, mapFeatureId: featureId };
      if (stage.mapFeatureId === featureId) return unlinkStageMapFeature(stage);
      return stage;
    });
    changeDraft({
      ...current,
      stages,
      map: { ...map, features: { type: 'FeatureCollection', features } },
    });
  };

  const flushSave = async (): Promise<void> => {
    if (savePromiseRef.current !== null) {
      await savePromiseRef.current;
      if (dirtyRef.current) await flushSave();
      return;
    }
    const editionId = selectedEditionRef.current;
    const snapshot = draftRef.current;
    if (!dirtyRef.current || editionId === undefined || snapshot === undefined) return;

    dirtyRef.current = false;
    setSaveState('saving');
    const request = adminApi
      .saveDraft(editionId, snapshot, etagRef.current)
      .then((saved) => {
        etagRef.current = saved.etag;
        if (draftRef.current === snapshot) {
          draftRef.current = saved.draft;
          setDraft(saved.draft);
          setSaveState('saved');
        } else {
          dirtyRef.current = true;
          setSaveState('dirty');
        }
      })
      .catch((error: unknown) => {
        dirtyRef.current = true;
        if (error instanceof ApiError && (error.status === 409 || error.status === 412)) {
          setSaveState('conflict');
        } else {
          setSaveState('error');
        }
        throw error;
      })
      .finally(() => {
        savePromiseRef.current = null;
      });
    savePromiseRef.current = request;
    await request;
  };

  useEffect(() => {
    if (saveState !== 'dirty') return;
    const timeout = window.setTimeout(() => {
      void flushSave().catch(() => undefined);
    }, 1_100);
    return () => window.clearTimeout(timeout);
  }, [draft, saveState]);

  useEffect(() => {
    const warnOnClose = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current && savePromiseRef.current === null) return;
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warnOnClose);
    return () => window.removeEventListener('beforeunload', warnOnClose);
  }, []);

  useEffect(() => {
    if (toast === undefined) return;
    const timeout = window.setTimeout(() => setToast(undefined), 4_000);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const createEdition = async () => {
    setCreating(true);
    try {
      await flushSave();
      const created = await adminApi.createEdition({
        ...newEdition,
        startsAt: toIso(newEdition.startsAt),
        endsAt: toIso(newEdition.endsAt),
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'editions'] });
      await switchEdition(created.id);
      setNewEditionOpen(false);
      setToast('Edition wurde als Entwurf angelegt.');
    } catch (error) {
      setToast(`Edition konnte nicht angelegt werden: ${errorMessage(error)}`);
    } finally {
      setCreating(false);
    }
  };

  const switchEdition = async (editionId: string) => {
    if (editionId === selectedEditionRef.current) return;
    try {
      await flushSave();
    } catch {
      setToast('Edition nicht gewechselt: Der aktuelle Entwurf konnte nicht gespeichert werden.');
      return;
    }
    setDraft(undefined);
    draftRef.current = undefined;
    dirtyRef.current = false;
    setValidation(undefined);
    setFocusFeatureId(undefined);
    setSelectedEditionId(editionId);
    setSection('content');
  };

  const validate = async (): Promise<ValidationResult | undefined> => {
    const editionId = selectedEditionId;
    if (editionId === undefined || draftRef.current === undefined) return undefined;
    setValidating(true);
    try {
      await flushSave();
      const result = await adminApi.validateDraft(editionId, draftRef.current);
      setValidation(result);
      setToast(result.valid ? 'Entwurf ist bereit.' : 'Validierung hat offene Punkte gefunden.');
      return result;
    } catch (error) {
      setToast(`Validierung fehlgeschlagen: ${errorMessage(error)}`);
      return undefined;
    } finally {
      setValidating(false);
    }
  };

  const publish = async () => {
    const editionId = selectedEditionId;
    if (editionId === undefined) return;
    setPublishing(true);
    try {
      await flushSave();
      const result = await adminApi.validateDraft(editionId, draftRef.current!);
      setValidation(result);
      if (!result.valid) {
        setToast('Release blockiert: Bitte Fehler beheben.');
        return;
      }
      const release = await adminApi.publishDraft(editionId, etagRef.current);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'editions'] }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'edition-revisions', editionId] }),
      ]);
      setToast(`Release v${release.version} ist jetzt öffentlich.`);
    } catch (error) {
      setToast(`Veröffentlichung fehlgeschlagen: ${errorMessage(error)}`);
    } finally {
      setPublishing(false);
    }
  };

  const restore = async (releaseId: string) => {
    if (selectedEditionId === undefined) return;
    try {
      const restored = await adminApi.restoreRevision(selectedEditionId, releaseId, etagRef.current);
      acceptServerDraft(restored);
      setToast('Revision wurde als neuer Entwurf wiederhergestellt.');
    } catch (error) {
      setToast(`Revision konnte nicht wiederhergestellt werden: ${errorMessage(error)}`);
    }
  };

  const importTimetable = async (source: string, format: 'csv' | 'json') => {
    if (selectedEditionId === undefined) return;
    setImporting(true);
    try {
      const imported = await adminApi.importTimetable(selectedEditionId, source, format, etagRef.current);
      acceptServerDraft(imported);
      setToast('Timetable wurde in den Entwurf übernommen.');
    } catch (error) {
      setToast(`Import fehlgeschlagen: ${errorMessage(error)}`);
      throw error;
    } finally {
      setImporting(false);
    }
  };

  const focusIssue = (issue: ValidationIssue) => {
    const root = issue.path[0];
    const stageMapIssue =
      root === 'stages' && issue.path.some((part) => part === 'mapFeatureId');
    if (root === 'map' || root === 'assets' || stageMapIssue) {
      setSection('map');
      setFocusFeatureId(root === 'map' ? issue.objectId : undefined);
      return;
    }
    setSection('content');
    window.setTimeout(() => {
      const target =
        issue.objectId !== undefined
          ? document.getElementById(`focus-${issue.objectId}`)
          : document.getElementById(root === 'stages' ? 'stages' : root === 'timetable' ? 'timetable' : 'edition-metadata');
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.classList.add('focus-flash');
      window.setTimeout(() => target?.classList.remove('focus-flash'), 1_600);
    }, 80);
  };

  const accessDenied =
    editions.error instanceof ApiError && [401, 403].includes(editions.error.status);

  if (accessDenied) {
    return (
      <main className="access-screen">
        <div className="access-mark">LIR</div>
        <span className="section-kicker">Edition Studio</span>
        <h1>Admin-Zugang erforderlich</h1>
        <p>Diese Oberfläche ist über Cloudflare Access geschützt. Bitte melde dich dort mit einem freigegebenen Konto an.</p>
        <button className="primary-button" type="button" onClick={() => window.location.reload()}>
          Zugang erneut prüfen
        </button>
      </main>
    );
  }

  return (
    <div className="studio-shell">
      <aside className="studio-sidebar">
        <div className="brand-row">
          <div className="brand-mark">L</div>
          <div>
            <strong>Edition Studio</strong>
            <span>Unofficial LIR Companion</span>
          </div>
        </div>
        <div className="sidebar-heading">
          <span>Editionen</span>
          <button type="button" aria-label="Neue Edition" onClick={() => setNewEditionOpen(true)}>
            +
          </button>
        </div>
        <nav className="edition-list" aria-label="Editionen">
          {editions.isLoading && <div className="sidebar-loading">Lädt …</div>}
          {editions.data?.map((edition) => (
            <button
              key={edition.id}
              type="button"
              className={selectedEditionId === edition.id ? 'edition-link is-active' : 'edition-link'}
              onClick={() => void switchEdition(edition.id)}
            >
              <span className={`edition-dot ${edition.status}`} />
              <span>
                <strong>{edition.title}</strong>
                <small>{edition.type === 'winter' ? 'Winter' : 'Sommer'} · {edition.status}</small>
              </span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span className="access-dot" />
          Geschützt durch Access
        </div>
      </aside>

      <div className="studio-main">
        <header className="studio-header">
          <div>
            <span className="section-kicker">LIR Companion V2</span>
            <h1>{draft?.edition.title.de ?? 'Edition auswählen'}</h1>
          </div>
          <div className="header-actions">
            {draft !== undefined && <span className={`save-status ${saveState}`}>{saveLabel(saveState)}</span>}
            <button className="avatar-button" type="button" title="Über Cloudflare Access angemeldet">
              AD
            </button>
          </div>
        </header>

        <nav className="section-tabs" aria-label="Studio-Bereiche">
          {(
            [
              ['content', 'Edition & Timetable'],
              ['map', 'Kartenstudio'],
              ['moderation', 'Moderation'],
              ['audit', 'Audit-Log'],
            ] as const
          ).map(([id, label]) => (
            <button
              className={section === id ? 'is-active' : ''}
              key={id}
              type="button"
              onClick={() => setSection(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        {saveState === 'conflict' && (
          <div className="save-banner conflict-banner" role="alert">
            <span>Dieser Entwurf wurde inzwischen in einem anderen Fenster geändert.</span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Lokale Änderungen verwerfen und den Serverstand laden?')) {
                  void draftQuery.refetch();
                }
              }}
            >
              Serverstand laden
            </button>
          </div>
        )}
        {saveState === 'error' && (
          <div className="save-banner error-banner" role="alert">
            <span>Der Entwurf konnte nicht gespeichert werden.</span>
            <button type="button" onClick={() => void flushSave().catch(() => undefined)}>
              Erneut versuchen
            </button>
          </div>
        )}

        <div className={section === 'map' ? 'studio-body map-body' : 'studio-body'}>
          {(section === 'content' || section === 'map') && selectedEditionId === undefined && (
            <div className="empty-state">
              <h2>Erste LIR-Edition anlegen</h2>
              <p>Erstelle einen Entwurf für Sommer oder Winter. Öffentlich sichtbar wird er erst nach Validierung und Veröffentlichung.</p>
              <button className="primary-button" type="button" onClick={() => setNewEditionOpen(true)}>
                Edition anlegen
              </button>
            </div>
          )}
          {(section === 'content' || section === 'map') && draftQuery.isLoading && (
            <div className="loading-panel large">Entwurf wird geladen …</div>
          )}
          {(section === 'content' || section === 'map') && draftQuery.isError && (
            <div className="error-box">Entwurf konnte nicht geladen werden.</div>
          )}
          {section === 'content' && draft !== undefined && (
            <DraftEditor
              draft={draft}
              validation={validation}
              revisions={revisions.data ?? []}
              validating={validating}
              publishing={publishing}
              importing={importing}
              onChange={changeDraft}
              onValidate={() => void validate()}
              onPublish={() => void publish()}
              onRestore={(releaseId) => void restore(releaseId)}
              onImport={importTimetable}
              onFocusIssue={focusIssue}
            />
          )}
          {section === 'map' && draft !== undefined && selectedEditionId !== undefined && (
            <Suspense fallback={<div className="loading-panel large">Kartenstudio wird geladen …</div>}>
              <MapEditor
                editionId={selectedEditionId}
                value={draft.map as MapDraftShape}
                stages={draft.stages}
                {...(focusFeatureId === undefined ? {} : { focusFeatureId })}
                onChange={changeMap}
                onStageLink={linkMapStage}
                onUpload={(file) => adminApi.uploadAsset(selectedEditionId, file, 'map-overlay')}
              />
            </Suspense>
          )}
          {section === 'moderation' && (
            <Suspense fallback={<div className="loading-panel large">Moderation wird geladen …</div>}>
              <ModerationStudio />
            </Suspense>
          )}
          {section === 'audit' && (
            <Suspense fallback={<div className="loading-panel large">Audit-Log wird geladen …</div>}>
              <AuditLog />
            </Suspense>
          )}
        </div>
      </div>

      {newEditionOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setNewEditionOpen(false)}>
          <section
            className="modal small-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-edition-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-heading">
              <div>
                <span className="section-kicker">Neuer Entwurf</span>
                <h2 id="new-edition-title">LIR-Edition anlegen</h2>
              </div>
              <button className="modal-close" type="button" onClick={() => setNewEditionOpen(false)}>
                ×
              </button>
            </div>
            <div className="field-stack">
              <label>
                Titel
                <input value={newEdition.title} onChange={(event) => setNewEdition({ ...newEdition, title: event.target.value })} />
              </label>
              <label>
                Variante
                <select
                  value={newEdition.type}
                  onChange={(event) => setNewEdition({ ...newEdition, type: event.target.value as 'summer' | 'winter' })}
                >
                  <option value="summer">Sommer</option>
                  <option value="winter">Winter</option>
                </select>
              </label>
              <div className="form-grid">
                <label>
                  Beginn
                  <input
                    type="datetime-local"
                    value={newEdition.startsAt}
                    onChange={(event) => setNewEdition({ ...newEdition, startsAt: event.target.value })}
                  />
                </label>
                <label>
                  Ende
                  <input
                    type="datetime-local"
                    value={newEdition.endsAt}
                    onChange={(event) => setNewEdition({ ...newEdition, endsAt: event.target.value })}
                  />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="secondary-button" type="button" onClick={() => setNewEditionOpen(false)}>
                Abbrechen
              </button>
              <button
                className="primary-button"
                type="button"
                disabled={creating || newEdition.title.trim().length === 0}
                onClick={() => void createEdition()}
              >
                {creating ? 'Wird angelegt …' : 'Entwurf anlegen'}
              </button>
            </div>
          </section>
        </div>
      )}

      {toast !== undefined && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}
